// ============================================================================

import {isCanonicalCommentAnchorHash} from './commentAnchorHash.ts';
// Canonical weigh-in session record route contract
// ============================================================================
//
// `{view: 'weighinsessions', paths: ['/weigh-in-sessions/*']}` is a SPLAT row
// in APP_ROUTES_POST_FORM, and `weighinsessions` has NO `VIEW_VARIANTS` entry.
// Both facts together are why this fence is wider in effect than any of its
// predecessors: `surfaceForRoutedView` short-circuits on the missing variant
// and answers `view.weighinsessions` for the ENTIRE `/weigh-in-sessions`
// space — the bare base, the splat base, every malformed id, and every
// view-led transition frame from a foreign pathname, which `routedLocationFor`
// canonicalizes onto the bare `/weigh-in-sessions`.
//
// MEASURED, not reasoned: with this surface added to the ledger and no fence,
// the real resolver answers RELEASED for all eighteen adversarial shapes
// probed — canonical, bare base, splat base, record trailing slash, extra
// nesting, `%2F`, `%5C`, `%00`, `%0A`, `%0D`, `%2E%2E`, a literal `..`, a
// malformed `%`, a search carrier, a non-canonical hash carrier, and three
// transition frames from foreign paths. Seventeen of those are not the audited direct-entry
// route.
//
// WHY THE SEVERITY IS HIGHER HERE THAN FOR ANY EARLIER FENCE. For every other
// released surface an unfenced non-canonical shape is a TRANSIENT frame that
// the App URL effect repairs a tick later. This surface has no repair path at
// all:
//
//   * `src/App.tsx` sets `view === 'weighinsessions'` while the pathname is the
//     bare splat base or starts with `/weigh-in-sessions/`;
//   * the same file's redirect effect returns early for exactly that pair, so
//     it never navigates away; and
//   * `VIEW_TO_PATH` has NO `weighinsessions` entry at all (stated in
//     `src/app/appRoutes.ts`), so the `pathFromView` repair can never fire.
//
// An unfenced RELEASED verdict would suppress recovery. A cold entry on any of
// those shapes would have nothing to render and nothing to heal it — a blank
// page that never repairs itself.
//
// It also matters WHAT the page would be handed. `WeighInSessionPage` derives
// its id with a naive `location.pathname.replace('/weigh-in-sessions/', '')`,
// so the canonicalized transition frame `/weigh-in-sessions` yields the literal
// id `/weigh-in-sessions` and the bare base yields itself unchanged. Keeping a
// garbage id out of a released render is precisely this module's job.
//
// ONE CLAUSE, NOT TWO. The Cattle and Layer index fences pair a routed-pathname
// equality with a raw-location predicate because their views DO have a
// `VIEW_VARIANTS` split, so the routed pathname carries information the raw
// location does not. Here it carries none: when the raw path is owned by the
// splat row, `routedLocationFor` returns it unchanged (routed === raw, so the
// raw predicate catches every malformed descendant), and when the raw path is
// NOT owned, routed collapses to the bare `/weigh-in-sessions` while the raw
// location is a foreign path (which the raw predicate also refuses). A second
// routed-pathname clause would be unfalsifiable here, and an unfalsifiable
// clause in a fence is worse than no clause: it reads as protection.
//
// RESERVED SEGMENTS: there are NONE beneath `/weigh-in-sessions/`.
// `src/lib/routes.ts` registers no `/weigh-in-sessions` path in `VIEW_TO_PATH`
// at all — this splat row in `src/app/appRoutes.ts` is the only declaration of
// the space — and the prefix appears in neither `ALIASES_EXACT` nor
// `ALIASES_PREFIX`. No sibling row in `APP_ROUTES_POST_FORM` nests inside it
// (unlike `/cattle/batches/forecast` or the Tasks router's `todo`), and
// `WeighInSessionPage` special-cases no id value. That absence is DERIVED in
// the release proof, not asserted here: a future `/weigh-in-sessions/archive`
// row would have to add a reserved set to this module AND appear in that
// proof; nothing else would notice it.
// ============================================================================

export const WEIGHIN_SESSION_RECORD_PATHNAME_PREFIX = '/weigh-in-sessions/';

type LocationLike = Readonly<{pathname?: unknown; search?: unknown; hash?: unknown}>;

// One meaningful raw segment: no separator in either direction, no NUL, no line
// break, and no query/fragment marker smuggled into a bare pathname string.
function isSafeRawSegment(rawSegment: string): boolean {
  return !(
    rawSegment === '' ||
    rawSegment.includes('/') ||
    rawSegment.includes('\\') ||
    rawSegment.includes('\0') ||
    rawSegment.includes('\r') ||
    rawSegment.includes('\n') ||
    rawSegment.includes('?') ||
    rawSegment.includes('#')
  );
}

// The same rules again after ONE decode, which is what turns `%2F`, `%5C`,
// `%00`, `%0A`, `%0D`, `%2E`, and `%2E%2E` into rejections rather than into
// accepted opaque text. A malformed escape makes decodeURIComponent throw and
// the caller's catch returns false.
function isSafeDecodedSegment(segment: string): boolean {
  return (
    segment.trim() !== '' &&
    segment !== '.' &&
    segment !== '..' &&
    !segment.includes('/') &&
    !segment.includes('\\') &&
    !segment.includes('\0') &&
    !segment.includes('\r') &&
    !segment.includes('\n') &&
    !segment.includes('?') &&
    !segment.includes('#')
  );
}

/**
 * `/weigh-in-sessions/<id>` with exactly one decoded-safe segment.
 *
 * The bare `/weigh-in-sessions` does not start with the prefix and the splat
 * base `/weigh-in-sessions/` ends at it, so this predicate claims neither — and
 * neither is a route any reviewer audited: there is no index page for this
 * surface at all. The raw segment is checked BEFORE the decoder so a literal
 * separator, NUL, or line break never reaches it, then the value is decoded
 * once and re-checked so an encoded separator, dot segment, NUL, or line break
 * cannot widen the record release.
 */
export function isCanonicalWeighInSessionRecordPathname(pathname: unknown): pathname is string {
  if (typeof pathname !== 'string' || !pathname.startsWith(WEIGHIN_SESSION_RECORD_PATHNAME_PREFIX)) {
    return false;
  }
  const rawSegment = pathname.slice(WEIGHIN_SESSION_RECORD_PATHNAME_PREFIX.length);
  if (!isSafeRawSegment(rawSegment)) return false;
  try {
    return isSafeDecodedSegment(decodeURIComponent(rawSegment));
  } catch {
    return false;
  }
}

/**
 * The record page READS `location.hash`: it scrolls a deep-linked
 * `#comment-<id>` mention from RecordCollaborationSection into view. That
 * exact, non-empty comment anchor is therefore an audited record render, not
 * a generic hash exception. Search, arbitrary non-empty hashes, and malformed
 * raw pathnames stay outside the release and receive explicit route recovery.
 * `tests/weighin_session_record_pages.spec.js` really does cold-enter this
 * route with a `#comment-<id>` fragment.
 *
 * A bare string is accepted only when it is plainly a pathname, so a caller or
 * test seam cannot smuggle an unsupported query or fragment into the target.
 */
export function isCanonicalWeighInSessionRecordLocation(location: unknown): boolean {
  if (typeof location === 'string') return isCanonicalWeighInSessionRecordPathname(location);
  if (!location || typeof location !== 'object' || Array.isArray(location)) return false;

  const {pathname, search, hash} = location as LocationLike;
  return (
    (search === undefined || search === '') &&
    (hash === undefined || hash === '' || isCanonicalCommentAnchorHash(hash)) &&
    isCanonicalWeighInSessionRecordPathname(pathname)
  );
}
